'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { FC, useEffect, useRef, useState } from 'react';
import Image from '@/components/Image';
import BreakdownItem from '@/components/LeadMagnet/Breakdown/Item';
import LeadMagnetProgress from '@/components/LeadMagnet/Progress';
import progressCss from '@/components/LeadMagnet/Progress/style.module.scss';
import AnimatedButton from '@/components/shared/AnimatedButton';
import AnimatedWavyBackground from '@/components/shared/AnimatedWavyBackground';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useModal } from '@/hooks/useModal';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { interpolateBlocks } from '@/lib/blocks-content';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { buildEntrance, prefersReducedMotion, splitTitleLines } from '@/lib/lead-magnet/animation';
import { formatProgressLabel, interpolateText } from '@/lib/lead-magnet/format';
import { resolveSignalIcon } from '@/lib/lead-magnet/icons';
import { FailedSignal } from '@/lib/lead-magnet/scoring';
import { LeadMagnetBreakdown as LeadMagnetBreakdownModel, LeadMagnetSocial } from '@/types/lead-magnet';
import css from './style.module.scss';
interface Props {
  breakdown: LeadMagnetBreakdownModel;
  signals: FailedSignal[];
  score: number;
  total: number;
  progressLabelFormat: string;
  socials: LeadMagnetSocial[];
}
const LeadMagnetBreakdown: FC<Props> = t0 => {
  const $ = _c(79);
  const {
    breakdown,
    signals,
    score,
    total,
    progressLabelFormat,
    socials
  } = t0;
  const device = useDevice();
  const isDesktop = device === "desktop";
  const {
    openModal
  } = useModal();
  const sectionRef = useRef(null);
  const glowRef = useRef(null);
  const ctaRef = useRef(null);
  const shouldLoadVideo = useLazyVideoSource(ctaRef);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [ctaRef];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useViewportVideosPlayback(t1);
  let t2;
  if ($[1] !== signals[0]?.slug) {
    t2 = () => signals[0]?.slug ?? null;
    $[1] = signals[0]?.slug;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [openSlug, setOpenSlug] = useState(t2);
  const [scrollToSlug, setScrollToSlug] = useState(null);
  let t3;
  let t4;
  if ($[3] !== isDesktop) {
    t3 = () => {
      const section = sectionRef.current;
      const glow = glowRef.current;
      if (!isDesktop || !section || !glow) {
        return;
      }
      const handleMouseMove = event => {
        gsap.to(glow, {
          x: event.clientX,
          y: event.clientY,
          duration: 1.4,
          ease: "power3.out",
          overwrite: "auto"
        });
      };
      section.addEventListener("mousemove", handleMouseMove);
      return () => section.removeEventListener("mousemove", handleMouseMove);
    };
    t4 = [isDesktop];
    $[3] = isDesktop;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const root = sectionRef.current;
      if (!root) {
        return;
      }
      const titleSplit = splitTitleLines(root, css.title);
      const timeline = buildEntrance([{
        targets: root.querySelector(`.${css.eyebrow}`),
        position: 0,
        duration: 0.6
      }, {
        targets: titleSplit.lines,
        role: "title",
        position: 0.1,
        duration: 1,
        stagger: 0.13
      }, {
        targets: root.querySelector(`.${css.description}`),
        position: 0.58,
        duration: 0.8
      }]);
      const segments = root.querySelectorAll(`.${progressCss.segment}`);
      if (segments.length && !prefersReducedMotion()) {
        gsap.set(segments, {
          scaleX: 0,
          opacity: 0,
          transformOrigin: "left center"
        });
        timeline.to(segments, {
          scaleX: 1,
          opacity: 1,
          duration: 0.4,
          stagger: 0.08,
          ease: "power2.out",
          clearProps: "transform"
        }, 0.72);
      }
      timeline.eventCallback("onComplete", titleSplit.revert);
      return titleSplit.revert;
    };
    t6 = {
      scope: sectionRef
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useGSAP(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const root_0 = sectionRef.current;
      if (!root_0 || prefersReducedMotion()) {
        return;
      }
      const cards = gsap.utils.toArray(`.${css.signals} > li`, root_0);
      if (!cards.length) {
        return;
      }
      gsap.set(cards, {
        opacity: 0,
        y: 24,
        filter: "blur(6px)"
      });
      const triggers = ScrollTrigger.batch(cards, {
        start: "top 88%",
        once: true,
        onEnter: _temp
      });
      document.fonts?.ready?.then(_temp2);
      return () => triggers.forEach(_temp3);
    };
    t8 = {
      scope: sectionRef
    };
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useGSAP(t7, t8);
  let t10;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const section_0 = ctaRef.current;
      if (!section_0) {
        return;
      }
      const titleSplit_0 = splitTitleLines(section_0, css.ctaTitle);
      const timeline_0 = buildEntrance([{
        targets: section_0.querySelector(`.${css.ctaMedia}`),
        role: "image",
        position: 0,
        duration: 1.3,
        vars: {
          ease: "power2.out"
        }
      }, {
        targets: titleSplit_0.lines,
        role: "title",
        position: 0,
        duration: 1,
        stagger: 0.13
      }, {
        targets: section_0.querySelector(`.${css.ctaDescription}`),
        position: 0.48,
        duration: 0.8,
        from: {
          y: 20,
          filter: "blur(7px)"
        }
      }, {
        targets: section_0.querySelector(`.${css.ctaButtonWrapper}`),
        position: 0.66,
        duration: 0.65,
        from: {
          y: 14,
          filter: "blur(0px)"
        }
      }], {
        scrollTrigger: {
          trigger: section_0,
          start: "top 70%",
          once: true
        }
      });
      timeline_0.eventCallback("onComplete", titleSplit_0.revert);
      return titleSplit_0.revert;
    };
    t10 = {
      scope: ctaRef
    };
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  useGSAP(t9, t10);
  const t11 = total - score;
  let cta;
  let description;
  let eyebrow;
  let scoreLabel;
  let t12;
  let title;
  if ($[12] !== breakdown || $[13] !== progressLabelFormat || $[14] !== score || $[15] !== t11 || $[16] !== total) {
    const tokens = {
      score,
      total,
      passed: score,
      failed: t11
    };
    eyebrow = breakdown.eyebrow ? interpolateText(breakdown.eyebrow, tokens) : null;
    title = interpolateBlocks(breakdown.title, tokens);
    description = interpolateBlocks(breakdown.description, tokens);
    let t13;
    if ($[23] !== progressLabelFormat || $[24] !== score || $[25] !== total) {
      t13 = formatProgressLabel(progressLabelFormat, {
        current: score,
        score,
        total
      });
      $[23] = progressLabelFormat;
      $[24] = score;
      $[25] = total;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    scoreLabel = t13;
    ({
      cta
    } = breakdown);
    t12 = interpolateBlocks(cta?.title ?? null, tokens);
    $[12] = breakdown;
    $[13] = progressLabelFormat;
    $[14] = score;
    $[15] = t11;
    $[16] = total;
    $[17] = cta;
    $[18] = description;
    $[19] = eyebrow;
    $[20] = scoreLabel;
    $[21] = t12;
    $[22] = title;
  } else {
    cta = $[17];
    description = $[18];
    eyebrow = $[19];
    scoreLabel = $[20];
    t12 = $[21];
    title = $[22];
  }
  const ctaTitle = t12;
  let t13;
  if ($[27] !== isDesktop) {
    t13 = isDesktop ? <div ref={glowRef} className={css.cursorGlow} aria-hidden="true" /> : null;
    $[27] = isDesktop;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <AnimatedWavyBackground className={css.heroWave} />;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== eyebrow) {
    t15 = eyebrow ? <Typography uppercase={true} variant="h3" tag="p" className={css.eyebrow}>{eyebrow}</Typography> : null;
    $[30] = eyebrow;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== title) {
    t16 = title ? <Typography uppercase={true} variant="h1" tag="h2" className={css.title}><StrapiRichContent isInlineContent={true} content={title} /></Typography> : null;
    $[32] = title;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== description) {
    t17 = description ? <Typography variant="body-l" tag="div" className={css.description}><StrapiRichContent content={description} /></Typography> : null;
    $[34] = description;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== score || $[37] !== scoreLabel || $[38] !== total) {
    t18 = <LeadMagnetProgress className={css.scoreBar} filled={score} total={total} label={scoreLabel} />;
    $[36] = score;
    $[37] = scoreLabel;
    $[38] = total;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18) {
    t19 = <header className={css.hero}>{t14}<div className={css.heroContent}>{t15}{t16}{t17}{t18}</div></header>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== breakdown.collapseLabel || $[46] !== breakdown.expandLabel || $[47] !== breakdown.recommendedActionLabel || $[48] !== openSlug || $[49] !== scrollToSlug || $[50] !== signals) {
    let t21;
    if ($[52] !== breakdown.collapseLabel || $[53] !== breakdown.expandLabel || $[54] !== breakdown.recommendedActionLabel || $[55] !== openSlug || $[56] !== scrollToSlug) {
      t21 = signal => <BreakdownItem key={signal.slug} insight={signal.resultInsight} icon={resolveSignalIcon(signal)} isOpen={openSlug === signal.slug} expandLabel={breakdown.expandLabel} collapseLabel={breakdown.collapseLabel} recommendedActionLabel={breakdown.recommendedActionLabel} shouldAutoScroll={scrollToSlug === signal.slug} onToggle={() => {
        const isOpening = openSlug !== signal.slug;
        setOpenSlug(isOpening ? signal.slug : null);
        setScrollToSlug(isOpening ? signal.slug : null);
      }} />;
      $[52] = breakdown.collapseLabel;
      $[53] = breakdown.expandLabel;
      $[54] = breakdown.recommendedActionLabel;
      $[55] = openSlug;
      $[56] = scrollToSlug;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    t20 = signals.map(t21);
    $[45] = breakdown.collapseLabel;
    $[46] = breakdown.expandLabel;
    $[47] = breakdown.recommendedActionLabel;
    $[48] = openSlug;
    $[49] = scrollToSlug;
    $[50] = signals;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[58] !== t20) {
    t21 = <ul className={css.signals}>{t20}</ul>;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== cta || $[61] !== ctaTitle || $[62] !== isDesktop || $[63] !== openModal || $[64] !== shouldLoadVideo) {
    t22 = cta ? <div className={css.ctaContent}>{ctaTitle ? <Typography uppercase={true} variant="h2" tag="h2" className={css.ctaTitle}><StrapiRichContent isInlineContent={true} content={ctaTitle} /></Typography> : null}{cta?.description ? <Typography variant="body-m" tag="div" className={css.ctaDescription}><StrapiRichContent content={cta.description} /></Typography> : null}{cta?.ctaLabel ? <AnimatedButton type={cta.ctaUrl ? "link" : "button"} href={cta.ctaUrl ?? undefined} wrapperClassName={css.ctaButtonWrapper} className={css.ctaButton} classNameText={css.ctaButtonLabel} onClick={cta.ctaUrl ? undefined : () => openModal("CALENDAR_BOOKING")}>{cta.ctaLabel}</AnimatedButton> : null}<div className={css.ctaMedia}>{cta?.image ? <Image className={css.ctaImage} src={cta.image.url} alt={cta.image.alt} width={cta.image.width ?? 960} height={cta.image.height ?? 640} sizes="(max-width: 991px) 100vw, 70vw" /> : <video loop={true} muted={true} playsInline={true} controls={false} className={css.ctaVideo} preload="none" poster="/assets/images/partnership/heads-pic.webp">{shouldLoadVideo ? isDesktop ? <><source src="/assets/video/webm/heads-pillow-letters.webm" type="video/webm" /><source src="/assets/video/mov/heads-pillow-letters.mov" type={"video/quicktime; codecs=\"hvc1\""} /></> : <><source src="/assets/video/mp4/feedback.mp4" type="video/mp4" /><source src="/assets/video/mov/qream-pillow-letters.mov" type={"video/quicktime; codecs=\"hvc1\""} /></> : null}</video>}</div></div> : null;
    $[60] = cta;
    $[61] = ctaTitle;
    $[62] = isDesktop;
    $[63] = openModal;
    $[64] = shouldLoadVideo;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== socials) {
    t23 = socials.length ? <ul className={css.socials}>{socials.map(_temp4)}</ul> : <span />;
    $[66] = socials;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Typography uppercase={true} variant="body-s" tag="p" className={css.copyright}>{`© Qream ${new Date().getFullYear()}`}</Typography>;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t23) {
    t25 = <footer className={css.pageFooter}>{t23}{t24}</footer>;
    $[69] = t23;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t22 || $[72] !== t25) {
    t26 = <aside ref={ctaRef} className={css.cta}>{t22}{t25}</aside>;
    $[71] = t22;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t13 || $[75] !== t19 || $[76] !== t21 || $[77] !== t26) {
    t27 = <section ref={sectionRef} className={css.breakdown}>{t13}{t19}{t21}{t26}</section>;
    $[74] = t13;
    $[75] = t19;
    $[76] = t21;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  return t27;
};
export default LeadMagnetBreakdown;
function _temp(batch) {
  return gsap.to(batch, {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    duration: 0.7,
    stagger: 0.08,
    ease: "power3.out",
    clearProps: "filter,transform"
  });
}
function _temp2() {
  return ScrollTrigger.refresh();
}
function _temp3(trigger) {
  return trigger.kill();
}
function _temp4(social) {
  return <li key={social.name}><a href={social.url} target={social.target ?? "_blank"} rel="noreferrer" className={css.socialLink}><Typography uppercase={true} variant="body-s" tag="span">{social.name}</Typography></a></li>;
}